import { useMutation, useApolloClient, ApolloError, useLazyQuery } from '@apollo/client'
import { addMonths, format } from 'date-fns'
import { Formik, getIn, useFormikContext } from 'formik'
import { isEmpty, keys } from 'lodash'
import { Fragment, ReactElement, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { safeFormatMessage } from '@10xlabs/utils'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { CenteredText } from 'components/Text'
import { IChangeDriverForm } from 'features/Trips/types'
import { DriverAutocomplete } from 'features/Drivers/components/DriverAutocomplete'
import { IDriverAutocomplete } from 'features/Drivers/components/MultiDriverAutocomplete'
import { GTTripsDriverConflictResponse, GTTripV2 } from 'gql/types.generated'
import { resolvers as tripsResolvers } from 'resolvers/trips'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import { Select } from 'components/v2/Select'
import { Textarea } from 'components/v2/Textarea'
import { WarningNotification } from 'components/WarningNotification'
import { getTripDateForOverride } from 'features/Trips/utils'
import { SCHEDULE_ROUTE_SERVICE_TRIPS } from 'features/Services/mutations-legacy'
import { GET_TRIPS_DRIVER_CONFLICT } from 'features/Trips/queries-legacy'
import { DATE_FORMAT } from 'utils/date'
import { getShortId } from 'utils/string'
import { CREATE_SERVICE_DRIVER_OVERRIDE } from 'features/Services/mutations'
import {
  CreateRouteServiceDriverOverrideReasonCategory,
  CreateRouteServiceDriverOverrideReasonSubcategory
} from 'gql/generated/graphql'

const messages = defineMessages({
  selectDriver: {
    id: 'trips.changeDriver.selectDriver',
    defaultMessage: 'Select new driver'
  },
  categoryPlaceholder: {
    id: 'trips.changeDriver.category.placeholder',
    defaultMessage: 'Select category'
  },
  subcategoryPlaceholder: {
    id: 'trips.changeDriver.subcategory.placeholder',
    defaultMessage: 'Select subcategory'
  },
  commentsPlaceholder: {
    id: 'trips.changeDriver.comments.placeholder',
    defaultMessage: 'Add comments'
  }
})

const changeDriverCategorysMessages = defineMessages({
  [CreateRouteServiceDriverOverrideReasonCategory.Partner]: {
    id: 'trips.changeDriver.category.partner',
    defaultMessage: 'Partner'
  },
  [CreateRouteServiceDriverOverrideReasonCategory.BusinessClient]: {
    id: 'trips.changeDriver.category.businessClient',
    defaultMessage: 'Business Client'
  },
  [CreateRouteServiceDriverOverrideReasonCategory.Driver]: {
    id: 'trips.changeDriver.category.driver',
    defaultMessage: 'Driver'
  },
  [CreateRouteServiceDriverOverrideReasonCategory.Other]: {
    id: 'trips.changeDriver.category.other',
    defaultMessage: 'Other'
  }
})
const changeDriverCausesMessages = defineMessages({
  [CreateRouteServiceDriverOverrideReasonSubcategory.Disability]: {
    id: 'trips.changeDriver.subcategory.disability',
    defaultMessage: 'Disability'
  },
  [CreateRouteServiceDriverOverrideReasonSubcategory.Absence]: {
    id: 'trips.changeDriver.subcategory.absence',
    defaultMessage: 'Absence'
  },
  [CreateRouteServiceDriverOverrideReasonSubcategory.Other]: {
    id: 'trips.changeDriver.subcategory.other',
    defaultMessage: 'Other'
  },
  [CreateRouteServiceDriverOverrideReasonSubcategory.Replacement]: {
    id: 'trips.changeDriver.subcategory.Replacement',
    defaultMessage: 'Replacement'
  }
})

export const CHANGE_DRIVER_REASON = {
  [CreateRouteServiceDriverOverrideReasonCategory.Driver]: {
    subcategories: [
      CreateRouteServiceDriverOverrideReasonSubcategory.Disability,
      CreateRouteServiceDriverOverrideReasonSubcategory.Absence,
      CreateRouteServiceDriverOverrideReasonSubcategory.Other
    ]
  },
  [CreateRouteServiceDriverOverrideReasonCategory.Partner]: {
    subcategories: [
      CreateRouteServiceDriverOverrideReasonSubcategory.Replacement,
      CreateRouteServiceDriverOverrideReasonSubcategory.Other
    ]
  },
  [CreateRouteServiceDriverOverrideReasonCategory.BusinessClient]: {
    subcategories: []
  },
  [CreateRouteServiceDriverOverrideReasonCategory.Other]: {
    subcategories: []
  }
}

const TripLink = styled(NavLink)`
  font-weight: 600;
  color: #5d59d7;
`

interface IChangeDriverFormProps {
  trip: GTTripV2
  onCancel: () => void
  loading?: boolean
  error?: ApolloError
}

const ChangeDriverForm = ({ trip, onCancel, loading, error }: IChangeDriverFormProps): ReactElement => {
  const intl = useIntl()
  const { setFieldValue, handleChange, handleBlur, handleSubmit, touched, errors, values } =
    useFormikContext<IChangeDriverForm>()
  const [getTripsDriverConflict, { data: tripsDriverConflictData, loading: tripsDriverConflictLoading }] =
    useLazyQuery<{ tripsDriverConflict: GTTripsDriverConflictResponse }>(GET_TRIPS_DRIVER_CONFLICT, {
      fetchPolicy: 'network-only'
    })
  useEffect(() => {
    const shouldVerifyPotentialConflicts = !isEmpty(values.driver.id) && values.driver.id !== trip.driver?.id
    if (shouldVerifyPotentialConflicts) {
      getTripsDriverConflict({
        variables: {
          input: {
            tripID: trip.id,
            driverID: values.driver.id
          }
        }
      })
    }
  }, [trip.id, values.driver.id, getTripsDriverConflict])

  const originName = trip.itinerary.at(0)?.place.name
  const destinationName = trip.itinerary.at(-1)?.place.name

  const totalConflict = tripsDriverConflictData?.tripsDriverConflict?.total || 0
  const tripsWithConflict = tripsDriverConflictData?.tripsDriverConflict?.items || []
  const tripsWithConflictIds = tripsWithConflict.map((trip) => trip.id)
  const today = new Date()
  const categorysOptions =
    CHANGE_DRIVER_REASON[values.category as CreateRouteServiceDriverOverrideReasonCategory]?.subcategories || []
  return (
    <form onSubmit={handleSubmit}>
      <Modal.Content>
        <Row>
          <Column>
            <div>
              <FormattedMessage
                id="trip.changeDriver.confirmation.tripDetails"
                defaultMessage="Trip from <strong>{origin}</strong> to <strong>{destination}</strong>"
                values={{
                  origin: originName,
                  destination: destinationName,
                  strong: (msg) => <strong>{msg}</strong>
                }}
              />
            </div>
          </Column>
        </Row>
        <Row>
          <Column>
            <DriverAutocomplete
              id="driver.id"
              placeholder={intl.formatMessage(messages.selectDriver)}
              value={values.driver.name}
              touched={getIn(touched, 'driver.id')}
              errors={getIn(errors, 'driver.id')}
              onChange={(driver: IDriverAutocomplete): void => {
                setFieldValue('driver', driver)
              }}
              label={<FormattedMessage id="trips.changeDriver.newDriver.label" defaultMessage="New driver" />}
              disabled={tripsDriverConflictLoading}
              isActive
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Select
              id="category"
              value={values.category}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.category}
              errors={errors.category}
              label={<FormattedMessage id="trips.changeDriver.form.category.label" defaultMessage="Category" />}
            >
              <option value="">{intl.formatMessage(messages.categoryPlaceholder)}</option>
              {keys(CHANGE_DRIVER_REASON).map((category): ReactElement => {
                return (
                  <option key={category} value={category}>
                    {safeFormatMessage({ intl, messages: changeDriverCategorysMessages, key: category })}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            <Select
              id="subcategory"
              value={values.subcategory}
              disabled={categorysOptions.length === 0}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.subcategory}
              errors={errors.subcategory}
              label={<FormattedMessage id="trips.changeDriver.form.subcategory.label" defaultMessage="Cause" />}
            >
              <option value="">{intl.formatMessage(messages.subcategoryPlaceholder)}</option>
              {categorysOptions?.map((subcategory): ReactElement => {
                return (
                  <option key={subcategory} value={subcategory}>
                    {safeFormatMessage({ intl, messages: changeDriverCausesMessages, key: subcategory })}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            <Textarea
              id="comments"
              label={<FormattedMessage id="trips.changeDriver.comments.label" defaultMessage="Comments" />}
              placeholder={intl.formatMessage(messages.commentsPlaceholder)}
              value={values.comments || ''}
              onChange={handleChange}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <CenteredText>
              <FormattedMessage
                id="trips.changeDriver.confirmation.text"
                defaultMessage="Are you sure you want to assign this driver?"
              />
            </CenteredText>
          </Column>
        </Row>
        {totalConflict > 0 && (
          <Row>
            <Column>
              <WarningNotification>
                <FormattedMessage
                  id="trips.changeDriver.conflict.desc"
                  defaultMessage="<strong>Driver {driverName} will merge with trip</strong> <tripIds></tripIds>. Do you want to continue with the adjustment?"
                  values={{
                    driverName: values.driver.name,
                    strong: (msg) => <strong>{msg}</strong>,
                    tripIds: () => (
                      <TripLink
                        to={`/trips?ids=${tripsWithConflictIds
                          .map((id) => getShortId(id))
                          .join(',')}&departureDateFrom=${format(
                          addMonths(today, -1),
                          DATE_FORMAT
                        )}&departureDateTo=${format(addMonths(today, 3), DATE_FORMAT)}`}
                      >
                        {tripsWithConflict.map((trip, index) => {
                          return (
                            <Fragment key={trip.id}>
                              {trip.id.substring(0, 8)}
                              {index < tripsWithConflict.length - 1 && ', '}
                            </Fragment>
                          )
                        })}
                      </TripLink>
                    )
                  }}
                />
              </WarningNotification>
            </Column>
          </Row>
        )}
      </Modal.Content>
      <Modal.Footer>
        {error && (
          <Modal.ErrorMessage>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="trips.changeDriver.error"
                defaultMessage="There was an error while assigning the driver"
              />
            </ErrorMessage>
          </Modal.ErrorMessage>
        )}
        <Modal.Actions>
          <Button type="button" kind="cancel" onClick={onCancel}>
            <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
          </Button>
          <Button type="submit" kind="primary" loading={loading} disabled={tripsDriverConflictLoading}>
            <FormattedMessage id="trips.changeDriver.action" defaultMessage="Assign driver" />
          </Button>
        </Modal.Actions>
      </Modal.Footer>
    </form>
  )
}
interface IChangeDriverModalProps {
  trip: GTTripV2
  closeModal: () => void
}

export const ChangeDriverModal = ({ trip, closeModal }: IChangeDriverModalProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(tripsResolvers)
  const driverRef = useRef<IChangeDriverForm['driver']>()

  const [
    createServiceDriverOverride,
    { loading: createServiceDriverOverrideLoading, error: createServiceDriverOverrideError }
  ] = useMutation(CREATE_SERVICE_DRIVER_OVERRIDE, {
    update: (cache): void => {
      if (trip.vehicle) {
        cache.modify({
          id: cache.identify(trip),
          fields: {
            driver: () => {
              return {
                id: driverRef.current?.id || '',
                firstName: driverRef.current?.name || '',
                lastName: '',
                RSVP: []
              }
            }
          }
        })
      }
    }
    // onError: (): void => {
    //   // Do nothing
    // }
  })
  const [scheduleServiceTrips, { loading: scheduleServiceTripsLoading, error: scheduleServiceTripsError }] =
    useMutation(SCHEDULE_ROUTE_SERVICE_TRIPS)

  const handleChangeDriver = async (values: IChangeDriverForm): Promise<void> => {
    try {
      driverRef.current = values.driver
      const tripDepartureAt = getTripDateForOverride(trip)
      await createServiceDriverOverride({
        variables: {
          input: {
            id: trip.routeService.id,
            date: tripDepartureAt,
            driverID: values.driver.id,
            reason: {
              category: values.category as CreateRouteServiceDriverOverrideReasonCategory,
              subcategory: (values.subcategory as CreateRouteServiceDriverOverrideReasonSubcategory) || null
            },
            comments: values.comments || null
          }
        }
      })
      await scheduleServiceTrips({
        variables: {
          input: {
            id: trip.routeService.id,
            scheduleStartAt: tripDepartureAt,
            scheduleEndAt: tripDepartureAt
          }
        }
      })

      closeModal()
    } catch (ex) {
      // DO NOTHING
    }
  }

  const initialValues: IChangeDriverForm = {
    driver: {
      id: trip.driver?.id || '',
      name: trip.driver?.firstName ? `${trip.driver?.firstName} ${trip.driver?.lastName}` : ''
    },
    category: '',
    subcategory: '',
    comments: ''
  }

  const loading = createServiceDriverOverrideLoading || scheduleServiceTripsLoading
  const error = createServiceDriverOverrideError || scheduleServiceTripsError

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="trips.changeDriver.title"
            defaultMessage="Assign driver for trip {id}"
            values={{
              id: trip.id.substring(0, 8)
            }}
          />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleChangeDriver} validationSchema={schema(intl)}>
        {(): ReactElement => {
          return <ChangeDriverForm trip={trip} onCancel={closeModal} loading={loading} error={error} />
        }}
      </Formik>
    </Modal.Container>
  )
}
